import { Time } from "@internationalized/date";
import {
  DateInput,
  DateSegment,
  FieldError,
  Group,
  I18nProvider,
  Label,
  TimeField as AriaTimeField,
} from "react-aria-components";
import { cn } from "@core/utils";

interface TimeFieldProps {
  /** Accessible label. Always required; hide it visually with `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  /** A 24-hour time (`09:00`), or an empty string while none is entered. */
  value: string;
  /** Called with `HH:mm`, or `""` while the time is incomplete. */
  onChange: (time: string) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  errorMessage?: string;
}

function toTime(value: string): Time | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  return match ? new Time(Number(match[1]), Number(match[2])) : null;
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * A 24-hour time (« 09:00 »), typed by segment, in the same box as the other fields. The value in
 * and out is a plain `HH:mm` string, so callers never see a library type. Always 24-hour and day
 * first, as the platform is written: the locale is pinned, not read from the browser.
 */
export function TimeField({
  label,
  hideLabel = false,
  value,
  onChange,
  onBlur,
  isDisabled = false,
  errorMessage,
}: TimeFieldProps) {
  const invalid = Boolean(errorMessage);

  return (
    <I18nProvider locale="fr-FR">
      <AriaTimeField
        value={toTime(value)}
        onChange={(time) => {
          onChange(time ? `${pad(time.hour)}:${pad(time.minute)}` : "");
        }}
        onBlur={onBlur}
        hourCycle={24}
        granularity="minute"
        shouldForceLeadingZeros
        isDisabled={isDisabled}
        isInvalid={invalid}
        validationBehavior="aria"
        className="flex w-full flex-col gap-1.5"
      >
        <Label
          className={cn(
            "text-sm-medium text-(--text-secondary)",
            hideLabel && "sr-only"
          )}
        >
          {label}
        </Label>
        <Group
          className={cn(
            "flex items-center rounded-md border px-3.5 py-2.5 transition-colors",
            isDisabled
              ? "bg-surface-field-disabled"
              : "bg-surface-field shadow-xs",
            invalid
              ? "border-(--border-field-error) focus-within:ring-1 focus-within:ring-(--border-field-error)"
              : "border-(--border-field) focus-within:border-(--border-field-focus) focus-within:ring-1 focus-within:ring-(--border-field-focus)"
          )}
        >
          <DateInput className="flex min-w-0 flex-1 items-center text-md-regular text-(--text-primary)">
            {(segment) => (
              <DateSegment
                segment={segment}
                className={cn(
                  "rounded-xs px-px tabular-nums outline-none",
                  "focus:bg-ink-100 data-[placeholder]:text-(--text-placeholder)",
                  "data-[disabled]:text-(--text-disabled)"
                )}
              />
            )}
          </DateInput>
        </Group>
        <FieldError className="text-sm-regular text-(--text-error)">
          {errorMessage}
        </FieldError>
      </AriaTimeField>
    </I18nProvider>
  );
}

export type { TimeFieldProps };
